import { types } from "mobx-state-tree";
import { NumberModel } from "../../mobxDataModels/numberModel";
import { StringModel } from "../../mobxDataModels/stringModel";
import { ListOfItemsModel } from "../../mobxDataModels/listOfItemsModel";
import {
  Mk8sDraftProviderAzureAMIModel,
  Mk8sDraftProviderAzureAMIReadOnlyModel,
} from "./mk8s.draft.provider.azure.ami";

export const Mk8sDraftProviderAzureNodePoolsReadonlyModel = types.model({
  name: types.optional(types.string, ""),
  labels: types.array(
    types.model({
      key: types.string,
      value: types.string,
    }),
  ),
  taints: types.array(
    types.model({
      key: types.string,
      value: types.string,
      effect: types.string,
    }),
  ),
  size: types.maybe(types.string),
  subnetId: types.maybe(types.string),
  zones: types.array(types.number),
  minSize: types.maybe(types.number),
  maxSize: types.maybe(types.number),
  overrideImage: types.optional(Mk8sDraftProviderAzureAMIReadOnlyModel, () =>
    Mk8sDraftProviderAzureAMIReadOnlyModel.create(),
  ),
  bootDiskSize: types.optional(types.number, 30),
});

export const Mk8sDraftProviderAzureNodePoolsModel = types
  .model({
    index: types.number,
    status: types.optional(types.enumeration(["default", "added"]), "default"),
    _pool: types.optional(Mk8sDraftProviderAzureNodePoolsReadonlyModel, () =>
      Mk8sDraftProviderAzureNodePoolsReadonlyModel.create(),
    ),
    name: types.optional(StringModel, () =>
      StringModel.create({ label: "Name", validationKey: "nodePoolName", isRequired: true }),
    ),
    labels: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    taints: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    size: types.optional(StringModel, () => StringModel.create({ label: "Size", isRequired: true })),
    subnetId: types.optional(StringModel, () => StringModel.create({ label: "Subnet ID", isRequired: true })),
    zones: types.optional(ListOfItemsModel, () => ListOfItemsModel.create()),
    minSize: types.optional(StringModel, () => NumberModel.create({ label: "Min Size", min: 0, isRequired: true })),
    maxSize: types.optional(StringModel, () => NumberModel.create({ label: "Max Size", min: 0, isRequired: true })),
    overrideImage: types.optional(Mk8sDraftProviderAzureAMIModel, () => Mk8sDraftProviderAzureAMIModel.create()),
    bootDiskSize: types.optional(StringModel, () =>
      NumberModel.create({ label: "Boot Disk Size (GB)", initialValue: 30, isRequired: true }),
    ),
  })
  .actions((self) => ({
    setStatusAdded() {
      self.status = "added";
    },
    reset() {
      self.name.setInitialValue(self._pool.name);
      self.labels = ListOfItemsModel.create({
        _items: self._pool.labels.map((l) => ({ firstValue: l.key, secondValue: l.value })),
      });
      self.taints = ListOfItemsModel.create({
        _items: self._pool.taints.map((t) => ({ firstValue: t.key, secondValue: t.value, thirdValue: t.effect })),
      });
      self.size.setInitialValue(self._pool.size || "");
      self.subnetId.setInitialValue(self._pool.subnetId || "");
      self.zones = ListOfItemsModel.create({
        _items: self._pool.zones.map((i) => ({ firstValue: String(i) })),
      });
      self.minSize.setInitialValue(String(self._pool.minSize).length > 0 ? String(self._pool.minSize) : "");
      self.maxSize.setInitialValue(String(self._pool.maxSize).length > 0 ? String(self._pool.maxSize) : "");
      const imageType = !!self._pool.overrideImage.reference
        ? "reference"
        : !!self._pool.overrideImage.recommended
        ? "recommended"
        : "none";
      self.overrideImage = Mk8sDraftProviderAzureAMIModel.create({
        reference_publisher: StringModel.create({
          label: "Publisher",
          initialValue: self._pool.overrideImage.reference?.publisher || "",
          isRequired: true,
        }),
        reference_offer: StringModel.create({
          label: "Offer",
          initialValue: self._pool.overrideImage.reference?.offer || "",
          isRequired: true,
        }),
        reference_sku: StringModel.create({
          label: "SKU",
          initialValue: self._pool.overrideImage.reference?.sku || "",
          isRequired: true,
        }),
        reference_version: StringModel.create({
          label: "Version",
          initialValue: self._pool.overrideImage.reference?.version || "",
          isRequired: true,
        }),
        recommended: self._pool.overrideImage.recommended,
        type: imageType,
      });
      self.bootDiskSize.setInitialValue(
        String(self._pool.bootDiskSize).length > 0 ? String(self._pool.bootDiskSize) : "",
      );
    },
    confirm() {
      self.name.confirm();
      self.labels.confirm();
      self.taints.confirm();
      self.size.confirm();
      self.subnetId.confirm();
      self.zones.confirm();
      self.minSize.confirm();
      self.maxSize.confirm();
      // no override image
      self.bootDiskSize.confirm();

      const _pool: any = {
        name: self.name.value, //
        labels: self.labels.items.map((l) => ({ key: l.firstValue, value: l.secondValue })),
        taints: self.taints.items.map((t) => ({ key: t.firstValue, value: t.secondValue, effect: t.thirdValue })),
        size: self.size.value,
        subnetId: self.subnetId.value,
        zones: self.zones.items.map((i) => Number(i.firstValue)),
        minSize: String(self.minSize.value).length > 0 ? Number(self.minSize.value) : undefined,
        maxSize: String(self.maxSize.value).length > 0 ? Number(self.maxSize.value) : undefined,
        overrideImage: {
          recommended: self.overrideImage.recommended,
          reference:
            self.overrideImage.type === "reference"
              ? {
                  publisher: self.overrideImage.reference_publisher.value,
                  offer: self.overrideImage.reference_offer.value,
                  sku: self.overrideImage.reference_sku.value,
                  version: self.overrideImage.reference_version.value,
                }
              : undefined,
        },
        bootDiskSize: Number(self.bootDiskSize.value),
      };

      self._pool = Mk8sDraftProviderAzureNodePoolsReadonlyModel.create(_pool);
    },
  }))
  .actions((self) => ({
    afterCreate() {
      self.reset();
    },
  }))
  .views((self) => ({
    get isLabelsValid() {
      let res = true;
      if (self.labels.items.some((l) => !l.firstValue || !l.secondValue)) res = false;
      return res;
    },
    get isTaintsValid() {
      let res = true;
      if (self.taints.items.some((t) => !t.firstValue || !t.secondValue || !t.thirdValue)) res = false;
      return res;
    },
  }))
  .views((self) => ({
    get isValid() {
      let res = true;
      if (!self.name.isValid) res = false;
      if (!self.isLabelsValid) res = false;
      if (!self.isTaintsValid) res = false;
      if (!self.size.isValid) res = false;
      if (!self.subnetId.isValid) res = false;
      // zones
      if (!self.minSize.isValid) res = false;
      if (!self.maxSize.isValid) res = false;
      // override image
      if (!self.bootDiskSize.isValid) res = false;
      return res;
    },
    get invalidReason() {
      let reason = "";
      if (!self.name.isValid) reason = "name is invalid";
      if (!self.isLabelsValid) reason = "labels not valid";
      if (!self.isTaintsValid) reason = "taints not valid";
      if (!self.size.isValid) reason = "size is invalid";
      if (!self.subnetId.isValid) reason = "subnetId is invalid";
      if (!self.minSize.isValid) reason = "minSize is invalid";
      if (!self.maxSize.isValid) reason = "maxSize is invalid";
      if (!self.bootDiskSize.isValid) reason = "bootDiskSize is invalid";
      return reason;
    },
    get isDirty() {
      let res = false;
      if (self.name.isDirty) res = true;
      if (self.labels.isDirty) res = true;
      if (self.taints.isDirty) res = true;
      if (self.size.isDirty) res = true;
      if (self.subnetId.isDirty) res = true;
      if (self.zones.isDirty) res = true;
      if (self.minSize.isDirty) res = true;
      if (self.maxSize.isDirty) res = true;

      const initialImageType = !!self._pool.overrideImage.reference
        ? "reference"
        : !!self._pool.overrideImage.recommended
        ? "recommended"
        : "none";
      if (initialImageType !== self.overrideImage.type) res = true;
      if (self.overrideImage.type === "reference") {
        if (self.overrideImage.reference_publisher.value !== self._pool.overrideImage.reference?.publisher) res = true;
        if (self.overrideImage.reference_offer.value !== self._pool.overrideImage.reference?.offer) res = true;
        if (self.overrideImage.reference_sku.value !== self._pool.overrideImage.reference?.sku) res = true;
        if (self.overrideImage.reference_version.value !== self._pool.overrideImage.reference?.version) res = true;
      } else if (self.overrideImage.type === "recommended") {
        if (self.overrideImage.recommended !== self._pool.overrideImage.recommended) res = true;
      }

      if (self.bootDiskSize.isDirty) res = true;
      return res;
    },
    get dirtyReason() {
      let reason = "";
      return reason;
    },
    get asObject() {
      const labels: { [_: string]: string } = {};
      self.labels.items.forEach((l) => {
        labels[l.firstValue] = l.secondValue;
      });

      const obj: any = {
        name: self.name.value,
        labels: labels,
        taints: self.taints.items.map((t) => ({ key: t.firstValue, value: t.secondValue, effect: t.thirdValue })),
        size: self.size.value,
        subnetId: self.subnetId.value,
        zones: self.zones.items.map((i) => Number(i.firstValue)),
      };

      if (self.overrideImage.type === "recommended") {
        obj.overrideImage = { recommended: self.overrideImage.recommended };
      } else if (self.overrideImage.type === "reference") {
        obj.overrideImage = {
          reference: {
            publisher: self.overrideImage.reference_publisher.value,
            offer: self.overrideImage.reference_offer.value,
            sku: self.overrideImage.reference_sku.value,
            version: self.overrideImage.reference_version.value,
          },
        };
      }

      if (self.minSize.value.length > 0) {
        obj.minSize = Number(self.minSize.value);
      }
      if (self.maxSize.value.length > 0) {
        obj.maxSize = Number(self.maxSize.value);
      }
      if (self.bootDiskSize.value.length > 0) {
        obj.bootDiskSize = Number(self.bootDiskSize.value);
      }
      return obj;
    },
  }));
